import { createSlice } from '@reduxjs/toolkit';
import referenceService from '../services/referenceService';
import type { TransportationType } from './models/transportation-type.model';
import type { LegalEntityType } from './models/legal-entity-type.model';
import type { LegalEntityStatus } from './models/legal-entity-status.model';
import type { StateCode } from './models/state-code.model';
import type { CountryList } from './models/country-list.model';
import type { MessagingMethod } from './models/messaging-method.model';
import type { InsuranceStrength } from './models/insurance-strength.model';
import type { VehicleAttributeType } from './models/vehicle-attribute-type.model';
import type { VehicleColor } from './models/vehicle-color.model';
import type { VehicleType } from './models/vehicle-type.model';
import type { ProviderNoteCategory } from './models/provider-note-category.model';
import type { ApplicationTitle } from './models/application-title.model';
import type { UserCategoryType } from './models/user-category-type.model';
import type { ApplicationSetting } from './models/application-setting.model';
import type {
  ProviderDocumentType,
  DriverDocumentType,
  VehicleDocumentType,
} from './models/document-type.model';
import type { TimeZone } from './models/timezone.model';
import type { FrequencyDayOfWeek } from './models/frequency-day-of-week.model';
import type { TripMode } from './models/trip-mode.model';
import type { TripLegPackageRejectReason } from './models/trip-leg-package-reject-reason.model';
import type { PaymentType } from './models/payment-type.model';
import type { ChargeType } from './models/charge-type.model';
import type { TripLegException } from './models/trip-leg-exception.model';
import type { AppDispatch } from './store';

interface ReferenceState {
  transportationTypes: TransportationType[];
  legalEntityStatuses: LegalEntityStatus[];
  legalEntityTypes: LegalEntityType[];
  stateCodes: StateCode[];
  insuranceStrengths: InsuranceStrength[];
  messagingMethods: MessagingMethod[];
  vehicleAttributeTypes: VehicleAttributeType[];
  vehicleColors: VehicleColor[];
  providerDocumentTypes: ProviderDocumentType[];
  driverDocumentTypes: DriverDocumentType[];
  vehicleDocumentTypes: VehicleDocumentType[];
  vehicleTypes: VehicleType[];
  providerNoteCategories: ProviderNoteCategory[];
  applicationTitles: ApplicationTitle[];
  userTypes: UserCategoryType[];
  applicationSettings: ApplicationSetting[];
  timeZones: TimeZone[];
  frequencyDayOfWeeks: FrequencyDayOfWeek[];
  tripModes: TripMode[];
  tripLegPackageRejectReasons: TripLegPackageRejectReason[];
  paymentTypes: PaymentType[];
  chargeTypes: ChargeType[];
  tripLegExceptions: TripLegException[];
}

export const initialState: ReferenceState = {
  transportationTypes: [],
  legalEntityStatuses: [],
  legalEntityTypes: [],
  stateCodes: [],
  insuranceStrengths: [],
  messagingMethods: [],
  vehicleAttributeTypes: [],
  vehicleColors: [],
  providerDocumentTypes: [],
  driverDocumentTypes: [],
  vehicleDocumentTypes: [],
  vehicleTypes: [],
  providerNoteCategories: [],
  applicationTitles: [],
  userTypes: [],
  applicationSettings: [],
  timeZones: [],
  frequencyDayOfWeeks: [],
  tripModes: [],
  tripLegPackageRejectReasons: [],
  paymentTypes: [],
  chargeTypes: [],
  tripLegExceptions: [],
};

export const slice = createSlice({
  name: 'reference',
  initialState,
  reducers: {
    setTransportationTypes: (
      state: ReferenceState,
      action: { payload: TransportationType[] },
    ) => {
      state.transportationTypes = action.payload;
    },
    setLegalEntityStatuses: (
      state: ReferenceState,
      action: { payload: LegalEntityStatus[] },
    ) => {
      state.legalEntityStatuses = action.payload;
    },
    setLegalEntityTypes: (
      state: ReferenceState,
      action: { payload: LegalEntityType[] },
    ) => {
      state.legalEntityTypes = action.payload;
    },
    setStateCodes: (
      state: ReferenceState,
      action: { payload: StateCode[] },
    ) => {
      state.stateCodes = action.payload;
    },
    setInsuranceStrengths: (
      state: ReferenceState,
      action: { payload: InsuranceStrength[] },
    ) => {
      state.insuranceStrengths = action.payload;
    },
    setMessagingMethods: (
      state: ReferenceState,
      action: { payload: MessagingMethod[] },
    ) => {
      state.messagingMethods = action.payload;
    },
    setVehicleAttributeTypes: (
      state: ReferenceState,
      action: { payload: VehicleAttributeType[] },
    ) => {
      state.vehicleAttributeTypes = action.payload;
    },
    setVehicleColors: (
      state: ReferenceState,
      action: { payload: VehicleColor[] },
    ) => {
      state.vehicleColors = action.payload;
    },
    setProviderDocumentTypes: (
      state: ReferenceState,
      action: { payload: ProviderDocumentType[] },
    ) => {
      state.providerDocumentTypes = action.payload;
    },
    setDriverDocumentTypes: (
      state: ReferenceState,
      action: { payload: DriverDocumentType[] },
    ) => {
      state.driverDocumentTypes = action.payload;
    },
    setVehicleDocumentTypes: (
      state: ReferenceState,
      action: { payload: VehicleDocumentType[] },
    ) => {
      state.vehicleDocumentTypes = action.payload;
    },
    setVehicleTypes: (
      state: ReferenceState,
      action: { payload: VehicleType[] },
    ) => {
      state.vehicleTypes = action.payload;
    },
    setProviderNoteCategories: (
      state: ReferenceState,
      action: { payload: ProviderNoteCategory[] },
    ) => {
      state.providerNoteCategories = action.payload;
    },
    setApplicationTitles: (
      state: ReferenceState,
      action: { payload: ApplicationTitle[] },
    ) => {
      state.applicationTitles = action.payload;
    },
    setUserTypes: (
      state: ReferenceState,
      action: { payload: UserCategoryType[] },
    ) => {
      state.userTypes = action.payload;
    },
    setApplicationSettings: (
      state: ReferenceState,
      action: { payload: ApplicationSetting[] },
    ) => {
      state.applicationSettings = action.payload;
    },
    setTimeZones: (state: ReferenceState, action: { payload: TimeZone[] }) => {
      state.timeZones = action.payload;
    },
    setFrequencyDayOfWeeks: (
      state: ReferenceState,
      action: { payload: FrequencyDayOfWeek[] },
    ) => {
      state.frequencyDayOfWeeks = action.payload;
    },
    setTripModes: (state: ReferenceState, action: { payload: TripMode[] }) => {
      state.tripModes = action.payload;
    },
    setTripLegPackageRejectReasons: (
      state: ReferenceState,
      action: { payload: TripLegPackageRejectReason[] },
    ) => {
      state.tripLegPackageRejectReasons = action.payload;
    },
    setPaymentTypes: (
      state: ReferenceState,
      action: { payload: PaymentType[] },
    ) => {
      state.paymentTypes = action.payload;
    },
    setChargeTypes: (
      state: ReferenceState,
      action: { payload: ChargeType[] },
    ) => {
      state.chargeTypes = action.payload;
    },
    setTripLegExceptions: (
      state: ReferenceState,
      action: { payload: TripLegException[] },
    ) => {
      state.tripLegExceptions = action.payload;
    },
  },
});

export const { setTransportationTypes } = slice.actions;
export const { setLegalEntityStatuses } = slice.actions;
export const { setLegalEntityTypes } = slice.actions;
export const { setStateCodes } = slice.actions;
export const { setInsuranceStrengths } = slice.actions;
export const { setMessagingMethods } = slice.actions;
export const { setVehicleAttributeTypes } = slice.actions;
export const { setVehicleColors } = slice.actions;
export const { setProviderDocumentTypes } = slice.actions;
export const { setDriverDocumentTypes } = slice.actions;
export const { setVehicleDocumentTypes } = slice.actions;
export const { setVehicleTypes } = slice.actions;
export const { setProviderNoteCategories } = slice.actions;
export const { setApplicationTitles } = slice.actions;
export const { setUserTypes } = slice.actions;
export const { setApplicationSettings } = slice.actions;
export const { setTimeZones } = slice.actions;
export const { setFrequencyDayOfWeeks } = slice.actions;
export const { setTripModes } = slice.actions;
export const { setTripLegPackageRejectReasons } = slice.actions;
export const { setPaymentTypes } = slice.actions;
export const { setChargeTypes } = slice.actions;
export const { setTripLegExceptions } = slice.actions;

export const fetchTransportationTypesAsync = () => (dispatch: AppDispatch) => {
  referenceService.getTransportationType().subscribe((types) => {
    dispatch(setTransportationTypes(types.filter((t) => t.isActive)));
  });
};

export const fetchLegalEntityStatusesAsync = () => (dispatch: AppDispatch) => {
  referenceService.getLegalEntityStatuses().subscribe((statuses) => {
    dispatch(setLegalEntityStatuses(statuses.filter((s) => s.isActive)));
  });
};

export const fetchLegalEntityTypesAsync = () => (dispatch: AppDispatch) => {
  referenceService.getLegalEntityTypes().subscribe((types) => {
    dispatch(setLegalEntityTypes(types.filter((t) => t.isActive)));
  });
};

export const fetchStateCodesAsync = () => (dispatch: AppDispatch) => {
  referenceService.getStateCodes().subscribe((codes) => {
    dispatch(
      setStateCodes(
        codes
          .filter((c) => c.isActive)
          .sort((a, b) => a.stateName.localeCompare(b.stateName)),
      ),
    );
  });
};

export const fetchInsuranceStrengthsAsync = () => (dispatch: AppDispatch) => {
  referenceService.getInsuranceStrengths().subscribe((strengths) => {
    dispatch(setInsuranceStrengths(strengths.filter((s) => s.isActive)));
  });
};

export const fetchMessagingMethodsAsync = () => (dispatch: AppDispatch) => {
  referenceService.getMessagingMethods().subscribe((strengths) => {
    dispatch(setMessagingMethods(strengths.filter((s) => s.isActive)));
  });
};

export const fetchVehicleAttributeTypesAsync =
  () => (dispatch: AppDispatch) => {
    referenceService.getVehicleAttributeTypes().subscribe((types) => {
      dispatch(setVehicleAttributeTypes(types.filter((t) => t.isActive)));
    });
  };

export const fetchVehicleColorsAsync = () => (dispatch: AppDispatch) => {
  referenceService.getVehicleColors().subscribe((colors) => {
    dispatch(setVehicleColors(colors.filter((c) => c.isActive)));
  });
};

export const fetchProviderDocumentTypesAsync =
  () => (dispatch: AppDispatch) => {
    referenceService.getProviderDocumentTypes().subscribe((types) => {
      dispatch(setProviderDocumentTypes(types));
    });
  };

export const fetchDriverDocumentTypesAsync = () => (dispatch: AppDispatch) => {
  referenceService.getDriverDocumentTypes().subscribe((types) => {
    dispatch(setDriverDocumentTypes(types));
  });
};

export const fetchVehicleDocumentTypesAsync = () => (dispatch: AppDispatch) => {
  referenceService.getVehicleDocumentTypes().subscribe((types) => {
    dispatch(setVehicleDocumentTypes(types));
  });
};

export const fetchVehicleTypesAsync = () => (dispatch: AppDispatch) => {
  referenceService.getVehicleTypes().subscribe((types) => {
    dispatch(setVehicleTypes(types.filter((t) => t.isActive)));
  });
};

export const fetchProviderNoteCategoriesAsync =
  () => (dispatch: AppDispatch) => {
    referenceService.getProviderNoteCategories().subscribe((categories) => {
      dispatch(setProviderNoteCategories(categories.filter((t) => t.isActive)));
    });
  };

export const fetchApplicationTitlesAsync = () => (dispatch: AppDispatch) => {
  referenceService.getApplicationTitles().subscribe((vals) => {
    dispatch(setApplicationTitles(vals));
  });
};

export const fetchUserTypesAsync = () => (dispatch: AppDispatch) => {
  referenceService.getUserTypes().subscribe((vals) => {
    dispatch(setUserTypes(vals));
  });
};

export const fetchApplicationSettingsAsync = () => (dispatch: AppDispatch) => {
  referenceService.getApplicationSettings().subscribe((vals) => {
    dispatch(setApplicationSettings(vals));
  });
};

export const fetchTimeZonesAsync = () => (dispatch: AppDispatch) => {
  referenceService.getTimeZones().subscribe((timezones) => {
    dispatch(setTimeZones(timezones));
  });
};

export const fetchFrequencyDayOfWeeksAsync = () => (dispatch: AppDispatch) => {
  referenceService.getFrequencyDayOfWeeks().subscribe((vals) => {
    dispatch(setFrequencyDayOfWeeks(vals));
  });
};

export const fetchTripModesAsync = () => (dispatch: AppDispatch) => {
  referenceService.getTripModes().subscribe((vals) => {
    dispatch(setTripModes(vals));
  });
};

export const fetchTripLegPackageRejectReasonsAsync =
  () => (dispatch: AppDispatch) => {
    referenceService.getTripLegPackageRejectReasons().subscribe((vals) => {
      dispatch(setTripLegPackageRejectReasons(vals));
    });
  };

export const fetchPaymentTypesAsync = () => (dispatch: AppDispatch) => {
  referenceService.getPaymentTypes().subscribe((vals) => {
    dispatch(setPaymentTypes(vals));
  });
};

export const fetchChargeTypesAsync = () => (dispatch: AppDispatch) => {
  referenceService.getChargeTypes().subscribe((vals) => {
    dispatch(setChargeTypes(vals));
  });
};

export const fetchTripLegExceptionsAsync = () => (dispatch: AppDispatch) => {
  referenceService.getTripLegExceptions().subscribe((vals) => {
    dispatch(setTripLegExceptions(vals));
  });
};

export const selectTransortationTypes = (state: {
  reference: ReferenceState;
}) => state.reference.transportationTypes;
export const selectLegalEntityStatuses = (state: {
  reference: ReferenceState;
}) => state.reference.legalEntityStatuses;
export const selectLegalEntityTypes = (state: { reference: ReferenceState }) =>
  state.reference.legalEntityTypes;
export const selectStateCodes = (state: { reference: ReferenceState }) =>
  state.reference.stateCodes;
export const selectInsuranceStrengths = (state: {
  reference: ReferenceState;
}) => state.reference.insuranceStrengths;
export const selectMessagingMethods = (state: { reference: ReferenceState }) =>
  state.reference.messagingMethods;
export const selectVehicleAttributeTypes = (state: {
  reference: ReferenceState;
}) => state.reference.vehicleAttributeTypes;
export const selectVehicleColors = (state: { reference: ReferenceState }) =>
  state.reference.vehicleColors;
export const selectProviderDocumentTypes = (state: {
  reference: ReferenceState;
}) => state.reference.providerDocumentTypes;
export const selectDriverDocumentTypes = (state: {
  reference: ReferenceState;
}) => state.reference.driverDocumentTypes;
export const selectVehicleDocumentTypes = (state: {
  reference: ReferenceState;
}) => state.reference.vehicleDocumentTypes;
export const selectVehicleTypes = (state: { reference: ReferenceState }) =>
  state.reference.vehicleTypes;
export const selectProviderNoteCategories = (state: {
  reference: ReferenceState;
}) => state.reference.providerNoteCategories;
export const selectApplicationTitles = (state: { reference: ReferenceState }) =>
  state.reference.applicationTitles;
export const selectUserTypes = (state: { reference: ReferenceState }) =>
  state.reference.userTypes;
export const selectApplicationSettings = (state: {
  reference: ReferenceState;
}) => state.reference.applicationSettings;
export const selectTimeZones = (state: { reference: ReferenceState }) =>
  state.reference.timeZones;
export const selectFrequencyDayOfWeeks = (state: {
  reference: ReferenceState;
}) => state.reference.frequencyDayOfWeeks;
export const selectTripModes = (state: { reference: ReferenceState }) =>
  state.reference.tripModes;
export const selectTripLegPackageRejectReasons = (state: {
  reference: ReferenceState;
}) => state.reference.tripLegPackageRejectReasons;
export const selectPaymentTypes = (state: { reference: ReferenceState }) =>
  state.reference.paymentTypes;
export const selectChargeTypes = (state: { reference: ReferenceState }) =>
  state.reference.chargeTypes;
export const selectTripLegExceptions = (state: { reference: ReferenceState }) =>
  state.reference.tripLegExceptions;

export default slice.reducer;
